import { Component, forwardRef, Inject, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from '../local-storage.service';
import { CognitoService } from '../service/cognito.service';
import { ValidationService } from '../service/validation.service';
import { ThemeService } from '../service/theme.service';
import { NavigationService } from '../service/navigation.service';
import { SystemMessageService } from '../service/system-message.service';
import { environment } from '../environments/environment';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { HttpClient, HttpHeaders } from '@angular/common/http';

interface Entity {
  id: string;
  name: string;
  phone_number: string;
  email: string;
  key?: string;
}

interface ApiKeyResponse{
  api_key : string;
}

@Component({
    selector: 'app-entities-api-management',
    templateUrl: './entities-api-management.component.html',
    styleUrl: './entities-api-management.component.css',
    standalone: false
})

export class EntitiesApiManagementComponent implements OnInit {
  constructor(
    @Inject(forwardRef(() => TranslateService))
    private translate: TranslateService,
    private cognitoService: CognitoService,
    public validationService: ValidationService,
    public theme: ThemeService,
    public navigationService: NavigationService,
    public systemMessage: SystemMessageService,
    private http: HttpClient,
    private localStorage: LocalStorageService
  ) {}

  public allEntities: Entity[] = [];
  public selectedEntity: Entity | undefined = undefined;
  public entityEmail: string | undefined = undefined;
  public entityPhone: string | undefined = undefined;
  public entityKey: string | undefined = undefined;
  public isEmailChecked: boolean = false;
  public isSmsChecked: boolean = false;

  ngOnInit() {
    try {
      // Role Check
      this.cognitoService.getCurrentRole(
        [environment.users.role.administrator],
        [environment.users.superAdmin]
      );

      // Get entities to fill the select element
      this.getAllEntities().then((entities) => {
        this.allEntities = entities;
        // check local storage for value of the last entity's id and set the page html element values
        /* if (this.localStorage.getItem('selected_entity') !== undefined) {
          this.setValuesOfLastEntity();
        } */
      });
    } catch (error) {
      console.error('Failed to load entities: ', error);
    }
  }

  // Set values to initialise here
  // CHECK IF THIS IS A WANTED BEHAVIOR
  setValuesOfLastEntity() {
    // Select the last entity that we generate a key for if it exist
    const previousId = this.localStorage.getItem('selected_entity') ?? undefined;
    this.selectedEntity = this.allEntities.find((entity) => entity.id === previousId);
    if (previousId !== null && this.selectedEntity !== undefined) {
      this.setEntityValues(); // set values of inputs
    }
  }

  // This is the onChange function of the <select> element for the entities
  async setEntityValues() {
    this.entityEmail = this.selectedEntity?.email ?? '';
    this.entityPhone = this.selectedEntity?.phone_number ?? '';
    this.entityPhone = this.entityPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    this.entityKey = ''
    
    const apiKey = await this.getEntityApiKey() ?? '';
    if(this.selectedEntity){
      this.selectedEntity.key = apiKey;
      this.entityKey = this.selectedEntity.key
    }
  }

  // MAIN FUNCTION
  async generateAction() {
    if (!this.selectedEntity || !this.selectedEntity.id) {
      console.error('No entity selected');
      return;
    }
    let userId: string = '';

    // Find the user id
    if (this.cognitoService.userInfos.attributes['custom:client_id']) {
      userId = this.cognitoService.userInfos.attributes['custom:client_id'];
    } else if (this.cognitoService.userInfos.attributes['custom:operations_id']) {
      userId = this.cognitoService.userInfos.attributes['custom:operations_id'];
    } else if (this.cognitoService.userInfos.attributes['custom:operator_id']) {
      userId = this.cognitoService.userInfos.attributes['custom:operator_id'];
    } else if (this.cognitoService.userInfos.attributes['custom:distributor_id']) {
      userId = this.cognitoService.userInfos.attributes['custom:distributor_id'];
    }
    if(userId === ''){
      console.error('No user id, aborting task! Contact your admin');
      return;
    }

    const message: string = await firstValueFrom(this.translate.get("generateKeyConfirm"));
    if(window.confirm(message)){
      const url = environment.api.stage + environment.api.route.saveApiKey;
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      try{
        const result = await firstValueFrom(
          this.http.post(
            url,
            { id: this.selectedEntity?.id, userId: userId, email: this.selectedEntity.email },
            { headers: headers }
          )
        );
        if (result) {
          this.localStorage.addItem('selected_entity', this.selectedEntity.id);
          window.location.reload();
        } else {
          throw new Error('No result after generating  key')
        }
      }
      catch(error){
        console.error(error)
        this.systemMessage.selectRibbon('danger', 'generateApiKeyError')
      }
    }
  }

  async getAllEntities(): Promise<Entity[]> {
    const url = environment.api.stage + environment.api.route.getAllEntitiesForApiManagement;
    const entities = await firstValueFrom(this.http.get<Entity[]>(url));
    if (entities && entities.length > 0) {
      return entities;
    }
    throw new Error('No entities found');
  }

  async getEntityApiKey(): Promise<string>{
    const url = environment.api.stage + environment.api.route.getApiKey;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    try {
      const response = await firstValueFrom(
        this.http.post<ApiKeyResponse>(url, { id: this.selectedEntity?.id }, { headers })
      );
      return response?.api_key ?? '';
    } catch (error) {
      // Backend currently returns 500 when no key exists; treat as empty key for UI flow.
      console.warn('getApiKey failed, falling back to empty key:', error);
      return '';
    }
  }

  async resendKeyToEmail(){
    const message: string = await firstValueFrom(this.translate.get("resendKey"));
    if(window.confirm(message)){
      const url = environment.api.stage + environment.api.route.resendKeyToEmail;
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const response = await firstValueFrom(this.http.post(url,{ id: this.selectedEntity?.id, email: this.selectedEntity?.email},{ headers: headers }))
      if(response){
        window.location.reload()
      }
    }
  }

  async copyToClipboard(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    console.log('Text successfully copied to clipboard');
  } catch (err) {
    console.error('Failed to copy text to clipboard:', err);
  }
}
}
